<template>
  <div class="feed-container">
    <!-- Welcome Header -->
    <div class="welcome-header">
      <div class="welcome-content">
        <div class="user-greeting">
          <img v-if="currentUser && currentUser.profile" :src="currentUser.profile" alt="Profile" class="user-avatar" />
          <div class="greeting-text">
              <h1 class="greeting-title">{{ $t('feed.welcomeBack', { name: currentUser ? currentUser.firstname : $t('feed.athlete') }) }}</h1>
              <p class="greeting-subtitle">{{ $t('feed.stayConnected') }}</p>
          </div>
        </div>
        <div class="quick-actions">
          <router-link v-if="$store.state.isLoggedIn" :to="{ name: 'profile' }" class="quick-action-btn profile-btn ai-powered">
            <div class="ai-particles">
              <div class="particle particle-1"></div>
              <div class="particle particle-2"></div>
              <div class="particle particle-3"></div>
              <div class="particle particle-4"></div>
            </div>
            <div class="profile-btn-content">
              <div class="profile-icon-wrapper">
                <font-awesome-icon icon="robot" class="profile-icon ai-icon" />
                <div class="profile-icon-glow"></div>
                <div class="ai-circuit"></div>
              </div>
              <div class="profile-text">
                <span class="profile-label">{{ $t('feed.profile') }}</span>
                <span class="profile-subtitle">{{ $t('feed.aiYourProfile') }}</span>
              </div>
              <div class="profile-arrow">
                <font-awesome-icon icon="magic" class="arrow-icon ai-sparkle" />
              </div>
            </div>
            <div class="profile-btn-shine"></div>
            <div class="ai-badge-floating">🤖 AI</div>
          </router-link>

          <router-link :to="{ name: 'events' }" class="quick-action-btn primary">
            <font-awesome-icon icon="calendar" />
            {{ $t('feed.joinEvent') }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Subscription Pricing Component -->
    <SubscriptionPricing />

    <!-- Main Feed Layout -->
    <div class="feed-layout">
      <!-- Main Feed -->
      <div class="main-feed">
        <!-- Activity Feed -->
        <div class="feed-section">
          <div class="feed-header">
            <h2 class="feed-title">
              ⚡ {{ $t('feed.recentActivity') }}
            </h2>
            <button @click="refreshFeed" class="refresh-btn" :disabled="loading">
              {{ loading ? '🔄' : '↻' }}
            </button>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <div class="loading-spinner">
              ⏳
            </div>
            <p>{{ $t('feed.loadingActivities') }}</p>
          </div>

          <!-- Activity Feed Items -->
          <div v-else class="activity-feed">
            <div v-for="activity in feedActivities" :key="activity.id" class="activity-card">
              <div class="activity-header">
                <a :href="activity.athlete.strava_link" target="_blank" class="athlete-link">
                  <img :src="activity.athlete.profile" :alt="activity.athlete.name" class="activity-avatar" />
                </a>
                <div class="activity-meta">
                  <div class="athlete-name">
                    <a :href="activity.athlete.strava_link" target="_blank">{{ activity.athlete.name }}</a>
                  </div>
                  <div class="activity-time">
                    <font-awesome-icon icon="clock" />
                    {{ formatActivityTime(activity.timestamp) }}
                  </div>
                </div>
                <div class="activity-type-badge">
                  <font-awesome-icon :icon="getActivityIcon(activity.type)" />
                  {{ activity.type }}
                </div>
              </div>

              <div class="activity-content">
                <div class="activity-title">{{ activity.name }}</div>

                <!-- AI Generated Video (prioritized if both image and video exist) -->
                <div v-if="activity.ai_video_url" class="ai-video-container">
                  <div class="video-wrapper">
                    <video
                      :src="activity.ai_video_url"
                      class="ai-generated-video"
                      controls
                      preload="metadata"
                      playsinline
                      :poster="activity.ai_image_url || ''"
                      @loadstart="onVideoLoadStart"
                      @loadeddata="onVideoLoaded"
                      @error="onVideoError"
                    >
                      <source :src="activity.ai_video_url" type="video/mp4">
                      {{ $t('feed.videoNotSupported') }}
                    </video>
                    <div class="video-controls-overlay">
                      <div class="play-button-overlay" @click="toggleVideoPlay" v-show="!isVideoPlaying">
                        <div class="play-icon">▶</div>
                      </div>
                    </div>
                  </div>
                  <div class="ai-badge ai-video-badge">
                    🎬 {{ $t('feed.aiGeneratedVideo') }}
                  </div>
                </div>

                <!-- AI Generated Image (only shown if no video exists) -->
                <div v-else-if="activity.ai_image_url" class="ai-image-container" @click="openImageModal(activity)">
                  <div class="image-wrapper">
                    <img
                      :src="activity.ai_image_url"
                      :alt="activity.name + ' - ' + $t('feed.aiGenerated')"
                      class="ai-generated-image"
                      loading="lazy"
                      @error="onImageError"
                    />
                    <div class="image-overlay">
                      <div class="overlay-content">
                        <span class="zoom-icon">🔍</span>
                        <span class="view-text">{{ $t('feed.viewFullSize') }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="ai-badge">
                    🤖 {{ $t('feed.aiGenerated') }}
                  </div>
                </div>

                <div class="activity-stats">
                  <div class="stat-item">
                    <font-awesome-icon icon="road" />
                    <span>{{ activity.km || 0 }} km</span>
                  </div>
                  <div v-if="activity.moving_time" class="stat-item">
                    <font-awesome-icon icon="clock" />
                    <span>{{ formatDuration(activity.moving_time) }}</span>
                  </div>
                  <div v-if="activity.average_speed" class="stat-item">
                    <font-awesome-icon icon="clock" />
                    <span>{{ activity.average_speed.toFixed(1) }} km/h</span>
                  </div>
                  <div v-if="activity.average_heartrate" class="stat-item">
                    ❤️
                    <span>{{ activity.average_heartrate }} bpm</span>
                  </div>
                  <div v-if="activity.average_cadence" class="stat-item cadence-stat">
                    🦶
                    <span>{{ activity.average_cadence }} spm</span>
                  </div>
                </div>
              </div>

              <div class="activity-actions">
                <button class="action-btn like-btn" @click="toggleLike(activity)">
                  <font-awesome-icon :icon="activity.liked ? 'heart' : 'heart'" :class="{ 'liked': activity.liked }" />
                  <span>{{ activity.likes || 0 }}</span>
                </button>
                <button class="action-btn comment-btn">
                  <font-awesome-icon icon="comment" />
                  <span>{{ activity.comments || 0 }}</span>
                </button>
                <button @click="goToProfile" class="action-btn ai-gen-btn" style="touch-action: manipulation;">
                  <font-awesome-icon icon="magic" class="btn-icon" />
                  <span class="action-text">AI Gen</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Load More Button -->
          <div v-if="feedActivities.length > 0" class="load-more-container">
            <button
              @click="loadMoreActivities"
              :disabled="loadingMore"
              class="load-more-btn"
            >
              <span v-if="loadingMore" class="loading-spinner">🏃‍♂️</span>
              <span v-else>{{ $t('feed.loadMore') || 'Load More Activities' }}</span>
            </button>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && feedActivities.length === 0" class="empty-state">
            <span>🏃</span>
            <h3>{{ $t('feed.noRecentActivities') }}</h3>
            <p>{{ $t('feed.followAthletes') }}</p>
            <router-link :to="{ name: 'events' }" class="cta-btn primary">
              {{ $t('feed.exploreEvents') }}
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Modal -->
    <div v-if="showImageModal" class="image-modal-overlay" @click="closeImageModal">
      <div class="image-modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">{{ (selectedActivity && selectedActivity.name) || $t('feed.activityImage') }}</h3>
          <button class="modal-close-btn" @click="closeImageModal">
            <font-awesome-icon icon="times" />
          </button>
        </div>
        <div class="modal-body">
          <img
            v-if="selectedActivity && selectedActivity.ai_image_url"
            :src="selectedActivity.ai_image_url"
            :alt="(selectedActivity.name || $t('feed.activity')) + ' - ' + $t('feed.aiGenerated')"
            class="modal-image"
            @error="onModalImageError"
          />
          <div class="modal-badge">
            🤖 {{ $t('feed.aiGeneratedImage') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $backend from '../backend'
import SubscriptionPricing from '@/components/SubscriptionPricing.vue'

export default {
  name: 'Feed',
  components: {
    SubscriptionPricing
  },
  metaInfo () {
    return {
      title: this.$t('feed.title') || 'Activity Feed'
    }
  },
  data () {
    return {
      feedActivities: [],
      currentUser: null,
      loading: false,
      loadingMore: false,
      error: '',
      isMobile: window.innerWidth <= 900,
      currentOffset: 0,
      limit: 10, // Will be set properly in created() hook
      showImageModal: false,
      selectedActivity: null,
      isVideoPlaying: false,
      currentVideo: null
    }
  },
  created () {
    // Set initial limit based on screen size
    this.limit = this.getPageLimit()

    this.loadCurrentUser()
    this.loadActivityFeed()
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('keydown', this.handleKeydown)
    // Restore body scroll in case modal was open
    document.body.style.overflow = 'auto'
  },
  methods: {
    handleResize () {
      this.isMobile = window.innerWidth <= 900

      // Update limit if device type changed
      const newLimit = this.isMobile ? 5 : 10
      if (this.limit !== newLimit) {
        this.limit = newLimit
        // Optionally reload feed with new limit
        // this.loadActivityFeed()
      }
    },

    handleKeydown (event) {
      // Close modal when ESC key is pressed
      if (event.key === 'Escape' && this.showImageModal) {
        this.closeImageModal()
      }
    },

    loadCurrentUser () {
      if (this.$store.state.isLoggedIn && localStorage.hasOwnProperty('athlete')) {
        const athleteData = localStorage.getItem('athlete')
        if (athleteData && athleteData !== 'undefined') {
          try {
            this.currentUser = JSON.parse(athleteData)
          } catch (e) {
            console.error('Error loading current user:', e)
            // Clear invalid data
            localStorage.removeItem('athlete')
          }
        }
      }
    },
    async loadActivityFeed (isLoadMore = false) {
      if (isLoadMore) {
        this.loadingMore = true
      } else {
        this.loading = true
        this.currentOffset = 0
        this.feedActivities = []
      }

      try {
        // Load activities using the dedicated feed endpoint
        const currentLimit = this.getPageLimit()
        const response = await $backend.getFeedActivities(this.currentOffset, currentLimit)
        const athletesData = response.data.athletes || {}

        // Transform athlete activities into feed items
        const activities = []
        Object.values(athletesData).forEach(athlete => {
          if (athlete.latest_activity) {
            activities.push({
              id: `${athlete._id}-${athlete.latest_activity.strava_id || Date.now()}`,
              name: athlete.latest_activity.name || 'Activity',
              type: athlete.latest_activity.type || 'Run',
              km: athlete.latest_activity.km || 0,
              moving_time: athlete.latest_activity.moving_time,
              average_speed: athlete.latest_activity.average_speed,
              timestamp: athlete.latest_activity.start_date_local || Date.now(),
              athlete: {
                name: athlete.name,
                profile: athlete.profile,
                strava_link: athlete.strava_link
              },
              likes: athlete.latest_activity.kudos_count || 0,
              comments: athlete.latest_activity.comment_count || 0,
              liked: false,
              // Additional fields from API
              ai_image_url: athlete.latest_activity.ai_image_url || null,
              ai_video_url: athlete.latest_activity.ai_video_url || null,
              average_cadence: athlete.latest_activity.average_cadence || null,
              elapsed_time: athlete.latest_activity.elapsed_time || null,
              has_heartrate: athlete.latest_activity.has_heartrate || false,
              max_heartrate: athlete.latest_activity.max_heartrate || null,
              average_heartrate: athlete.latest_activity.average_heartrate || null
            })
          }
        })

        // Sort by most recent activities
        const sortedActivities = activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))

        if (isLoadMore) {
          this.feedActivities = [...this.feedActivities, ...sortedActivities]
        } else {
          this.feedActivities = sortedActivities
        }

        // Update pagination state
        this.currentOffset += currentLimit
      } catch (error) {
        console.error('Error loading activity feed:', error)
        this.error = 'Failed to load activity feed'
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    async loadMoreActivities () {
      if (this.loadingMore) return
      await this.loadActivityFeed(true)
    },

    getPageLimit () {
      // Use 5 items per page on mobile for better performance and UX
      // Use 10 items per page on desktop for more content visibility
      return this.isMobile ? 5 : 10
    },
    async refreshFeed () {
      await this.loadActivityFeed()
    },
    toggleLike (activity) {
      activity.liked = !activity.liked
      if (activity.liked) {
        activity.likes = (activity.likes || 0) + 1
      } else {
        activity.likes = Math.max(0, (activity.likes || 0) - 1)
      }
    },

    goToProfile () {
      this.$router.push({ name: 'profile' })
    },

    openImageModal (activity) {
      if (activity.ai_image_url) {
        this.selectedActivity = activity
        this.showImageModal = true
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden'
      }
    },

    closeImageModal () {
      this.showImageModal = false
      this.selectedActivity = null
      // Restore body scroll
      document.body.style.overflow = 'auto'
    },

    // Video-related methods
    toggleVideoPlay (event) {
      const video = event.target.closest('.ai-video-container').querySelector('video')
      if (video) {
        if (video.paused) {
          this.playVideo(video)
        } else {
          this.pauseVideo(video)
        }
      }
    },

    playVideo (video) {
      // Pause any currently playing video
      if (this.currentVideo && this.currentVideo !== video) {
        this.currentVideo.pause()
      }

      video.play().then(() => {
        this.isVideoPlaying = true
        this.currentVideo = video
      }).catch(error => {
        console.error('Error playing video:', error)
      })
    },

    pauseVideo (video) {
      video.pause()
      this.isVideoPlaying = false
      if (this.currentVideo === video) {
        this.currentVideo = null
      }
    },

    onVideoLoadStart () {
      console.log('Video load started')
    },

    onVideoLoaded () {
      console.log('Video loaded successfully')
    },

    onVideoError (event) {
      console.error('Video loading error:', event)
      console.log('Video URL that failed:', event.target.src)

      // Hide the video container when URL is invalid/expired
      const videoElement = event.target
      const container = videoElement.closest('.ai-video-container')
      if (container) {
        container.style.display = 'none'
        console.log('Hiding video container due to loading error')

        // Also remove the URL from the activity data to prevent future attempts
        const activityIndex = this.feedActivities.findIndex(a => a.ai_video_url === videoElement.src)
        if (activityIndex !== -1) {
          this.feedActivities[activityIndex].ai_video_url = null
          console.log('Removed expired video URL from activity data')
        }
      }
    },

    onImageError (event) {
      console.error('Image loading error:', event)
      console.log('Image URL that failed:', event.target.src)

      // Hide the image container when URL is invalid/expired
      const imgElement = event.target
      const container = imgElement.closest('.ai-image-container')
      if (container) {
        container.style.display = 'none'
        console.log('Hiding image container due to loading error')

        // Also remove the URL from the activity data to prevent future attempts
        const activityIndex = this.feedActivities.findIndex(a => a.ai_image_url === imgElement.src)
        if (activityIndex !== -1) {
          this.feedActivities[activityIndex].ai_image_url = null
          console.log('Removed expired image URL from activity data')
        }
      }
    },

    onModalImageError (event) {
      console.error('Modal image loading error:', event)
      // Close the modal when image fails to load
      this.closeImageModal()
      // Also remove the URL from the activity data
      if (this.selectedActivity) {
        this.selectedActivity.ai_image_url = null
        // Find and update the activity in the feedActivities array
        const activityIndex = this.feedActivities.findIndex(a => a.id === this.selectedActivity.id)
        if (activityIndex !== -1) {
          this.feedActivities[activityIndex].ai_image_url = null
        }
      }
    },

    isValidUrl (url) {
      if (!url) return false

      // Basic URL format validation
      try {
        // eslint-disable-next-line no-unused-vars
        const validUrl = new URL(url)
        return true
      } catch (e) {
        return false
      }
    },

    getActivityIcon (type) {
      switch (type) {
        case 'Swim':
          return 'swimmer'
        case 'Ride':
          return 'biking'
        case 'Run':
          return 'running'
        case 'Walk':
          return 'walking'
        default:
          return 'info'
      }
    },
    formatActivityTime (timestamp) {
      if (!timestamp) return 'N/A'
      const date = new Date(timestamp)
      const now = new Date()
      const diffMs = now - date
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffDays = Math.floor(diffHours / 24)

      if (diffDays > 0) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
      } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
      } else {
        const diffMins = Math.floor(diffMs / (1000 * 60))
        return `${Math.max(1, diffMins)} minute${diffMins > 1 ? 's' : ''} ago`
      }
    },
    formatDuration (seconds) {
      if (!seconds) return 'N/A'
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      if (hours > 0) {
        return `${hours}h ${minutes}m`
      }
      return `${minutes}m`
    }
  },

  computed: {
    isLoggedIn () {
      return this.$store.state.isLoggedIn
    },

    filteredActivities () {
      // Simply return the activities as-is, let error handlers deal with expired URLs
      return this.feedActivities
    }
  }
}
</script>

<style scoped>
/* Sport light-blue theme */
/* Feed Container */
.feed-container {
  font-family: 'Inter', Arial, sans-serif;
  background: linear-gradient(135deg, #fafcff 0%, #f0f9ff 30%, rgba(224, 242, 254, 0.4) 70%, #cffafe 100%);
  color: #222;
  min-height: 100vh;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

/* Welcome Header - Lucky & Energetic Theme */
.welcome-header {
  background: linear-gradient(135deg, #0c4a6e 0%, #0ea5e9 50%, #38bdf8 100%);
  color: #fff;
  border-radius: 1.5rem;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 8px 30px rgba(12, 74, 110, 0.25);
  border: 3px solid #7dd3fc;
}

.welcome-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.user-greeting {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.user-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, 0.2);
  object-fit: cover;
}

.greeting-title {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.greeting-subtitle {
  font-size: 1rem;
  opacity: 0.9;
}

.quick-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.quick-action-btn {
  padding: 0.75rem 1.5rem;
  border-radius: 2rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.quick-action-btn.primary {
  background: linear-gradient(135deg, #38bdf8, #7dd3fc);
  color: #0c4a6e;
}

.quick-action-btn.primary:hover {
  background: linear-gradient(135deg, #7dd3fc, #38bdf8);
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 6px 20px rgba(56, 189, 248, 0.4);
}

.quick-action-btn.secondary {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.quick-action-btn.secondary:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

/* Enhanced AI Profile Button Styles */
.quick-action-btn.profile-btn.ai-powered {
  background: linear-gradient(135deg, #00d4ff 0%, #5b21b6 25%, #7c3aed 50%, #ec4899 75%, #f59e0b 100%);
  color: #fff;
  border: 2px solid rgba(0, 212, 255, 0.3);
  position: relative;
  overflow: hidden;
  padding: 0;
  min-width: 220px;
  height: 65px;
  border-radius: 32px;
  box-shadow:
    0 8px 32px rgba(0, 212, 255, 0.4),
    0 4px 16px rgba(91, 33, 182, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.3),
    0 0 20px rgba(0, 212, 255, 0.2);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ai-glow 3s ease-in-out infinite;
}

@keyframes ai-glow {
  0%, 100% {
    box-shadow:
      0 8px 32px rgba(0, 212, 255, 0.4),
      0 4px 16px rgba(91, 33, 182, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.3),
      0 0 20px rgba(0, 212, 255, 0.2);
  }
  50% {
    box-shadow:
      0 8px 40px rgba(0, 212, 255, 0.6),
      0 6px 20px rgba(91, 33, 182, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.4),
      0 0 30px rgba(0, 212, 255, 0.4);
  }
}

.quick-action-btn.profile-btn.ai-powered::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.6), transparent);
  transition: left 0.8s ease;
}

.quick-action-btn.profile-btn.ai-powered:hover::before {
  left: 100%;
}

.quick-action-btn.profile-btn.ai-powered:hover {
  transform: translateY(-4px) scale(1.03);
  box-shadow:
    0 15px 45px rgba(0, 212, 255, 0.5),
    0 10px 25px rgba(91, 33, 182, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.4),
    0 0 40px rgba(0, 212, 255, 0.6);
  border-color: rgba(0, 212, 255, 0.6);
}

.quick-action-btn.profile-btn.ai-powered:active {
  transform: translateY(-2px) scale(0.98);
  transition: all 0.1s ease;
}

/* AI Particles Animation */
.ai-particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: rgba(0, 212, 255, 0.8);
  border-radius: 50%;
  animation: float-particle 4s ease-in-out infinite;
}

.particle-1 {
  top: 20%;
  left: 10%;
  animation-delay: 0s;
  background: rgba(0, 212, 255, 0.9);
}

.particle-2 {
  top: 60%;
  right: 15%;
  animation-delay: 1s;
  background: rgba(236, 72, 153, 0.8);
}

.particle-3 {
  top: 40%;
  left: 20%;
  animation-delay: 2s;
  background: rgba(245, 158, 11, 0.8);
}

.particle-4 {
  top: 80%;
  right: 25%;
  animation-delay: 3s;
  background: rgba(124, 58, 237, 0.8);
}

@keyframes float-particle {
  0%, 100% {
    transform: translateY(0) scale(1);
    opacity: 0.6;
  }
  25% {
    transform: translateY(-10px) scale(1.2);
    opacity: 1;
  }
  50% {
    transform: translateY(-5px) scale(0.8);
    opacity: 0.8;
  }
  75% {
    transform: translateY(-15px) scale(1.1);
    opacity: 0.9;
  }
}

.quick-action-btn.profile-btn.ai-powered:hover .particle {
  animation-duration: 2s;
  opacity: 1;
}

.profile-btn-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 20px;
  position: relative;
  z-index: 2;
  width: 100%;
  justify-content: space-between;
}

.profile-icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.3), rgba(91, 33, 182, 0.3));
  border-radius: 50%;
  backdrop-filter: blur(15px);
  border: 2px solid rgba(0, 212, 255, 0.5);
  transition: all 0.3s ease;
  box-shadow:
    0 0 20px rgba(0, 212, 255, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.profile-icon.ai-icon {
  font-size: 20px;
  color: #00d4ff;
  z-index: 2;
  position: relative;
  transition: all 0.3s ease;
  filter: drop-shadow(0 0 8px rgba(0, 212, 255, 0.6));
  animation: ai-icon-pulse 2s ease-in-out infinite;
}

@keyframes ai-icon-pulse {
  0%, 100% {
    transform: scale(1);
    filter: drop-shadow(0 0 8px rgba(0, 212, 255, 0.6));
  }
  50% {
    transform: scale(1.1);
    filter: drop-shadow(0 0 12px rgba(0, 212, 255, 0.8));
  }
}

.profile-icon-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(0, 212, 255, 0.6) 0%, transparent 70%);
  border-radius: 50%;
  transform: translate(-50%, -50%) scale(0);
  transition: all 0.3s ease;
  animation: ai-pulse-glow 2s ease-in-out infinite;
}

@keyframes ai-pulse-glow {
  0%, 100% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.3);
    opacity: 0.8;
  }
}

.ai-circuit {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: circuit-rotate 4s linear infinite;
}

.ai-circuit::before,
.ai-circuit::after {
  content: '';
  position: absolute;
  width: 2px;
  height: 2px;
  background: #00d4ff;
  border-radius: 50%;
  box-shadow: 0 0 4px #00d4ff;
}

.ai-circuit::before {
  top: 10%;
  left: 50%;
  transform: translateX(-50%);
}

.ai-circuit::after {
  bottom: 10%;
  right: 20%;
}

@keyframes circuit-rotate {
  0% { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg); }
}

.quick-action-btn.profile-btn.ai-powered:hover .profile-icon-wrapper {
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.4), rgba(91, 33, 182, 0.4));
  border-color: rgba(0, 212, 255, 0.7);
  transform: scale(1.15);
  box-shadow:
    0 0 30px rgba(0, 212, 255, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.quick-action-btn.profile-btn.ai-powered:hover .profile-icon.ai-icon {
  transform: scale(1.2);
  color: #00d4ff;
  filter: drop-shadow(0 0 15px rgba(0, 212, 255, 1));
}

.quick-action-btn.profile-btn.ai-powered:hover .profile-icon-glow {
  transform: translate(-50%, -50%) scale(1.8);
  opacity: 1;
}

.quick-action-btn.profile-btn.ai-powered:hover .ai-circuit {
  border-color: rgba(0, 212, 255, 0.6);
  animation-duration: 2s;
}

.profile-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  margin-left: 8px;
  text-align: center;
}

.profile-label {
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  line-height: 1.2;
  text-shadow:
    0 1px 2px rgba(0, 0, 0, 0.3),
    0 0 10px rgba(0, 212, 255, 0.5);
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #fff, #00d4ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: text-glow 3s ease-in-out infinite;
}

@keyframes text-glow {
  0%, 100% {
    text-shadow:
      0 1px 2px rgba(0, 0, 0, 0.3),
      0 0 10px rgba(0, 212, 255, 0.5);
  }
  50% {
    text-shadow:
      0 1px 2px rgba(0, 0, 0, 0.3),
      0 0 15px rgba(0, 212, 255, 0.8);
  }
}

.profile-subtitle {
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 212, 255, 0.9);
  line-height: 1;
  margin-top: 3px;
  text-shadow:
    0 1px 2px rgba(0, 0, 0, 0.2),
    0 0 8px rgba(0, 212, 255, 0.4);
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
}

.quick-action-btn.profile-btn.ai-powered:hover .profile-label {
  transform: translateY(-2px);
  text-shadow:
    0 2px 4px rgba(0, 0, 0, 0.4),
    0 0 20px rgba(0, 212, 255, 0.8);
  animation-duration: 1.5s;
}

.quick-action-btn.profile-btn.ai-powered:hover .profile-subtitle {
  color: rgba(0, 212, 255, 1);
  transform: translateY(-1px);
  text-shadow:
    0 1px 2px rgba(0, 0, 0, 0.3),
    0 0 12px rgba(0, 212, 255, 0.6);
}

.profile-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.2), rgba(91, 33, 182, 0.2));
  border-radius: 50%;
  backdrop-filter: blur(15px);
  border: 2px solid rgba(0, 212, 255, 0.4);
  transition: all 0.3s ease;
  box-shadow:
    0 0 15px rgba(0, 212, 255, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.arrow-icon.ai-sparkle {
  font-size: 16px;
  color: #00d4ff;
  transition: all 0.3s ease;
  filter: drop-shadow(0 0 6px rgba(0, 212, 255, 0.8));
  animation: sparkle-rotate 2s ease-in-out infinite;
}

@keyframes sparkle-rotate {
  0%, 100% {
    transform: rotate(0deg) scale(1);
    filter: drop-shadow(0 0 6px rgba(0, 212, 255, 0.8));
  }
  50% {
    transform: rotate(180deg) scale(1.1);
    filter: drop-shadow(0 0 10px rgba(0, 212, 255, 1));
  }
}

.quick-action-btn.profile-btn.ai-powered:hover .profile-arrow {
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.3), rgba(91, 33, 182, 0.3));
  border-color: rgba(0, 212, 255, 0.6);
  transform: scale(1.15);
  box-shadow:
    0 0 25px rgba(0, 212, 255, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.quick-action-btn.profile-btn.ai-powered:hover .arrow-icon.ai-sparkle {
  transform: translateX(4px) rotate(180deg) scale(1.2);
  color: #00d4ff;
  filter: drop-shadow(0 0 12px rgba(0, 212, 255, 1));
  animation-duration: 1s;
}

/* Floating AI Badge */
.ai-badge-floating {
  position: absolute;
  top: -8px;
  right: -8px;
  background: linear-gradient(135deg, #00d4ff, #5b21b6);
  color: #fff;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 700;
  box-shadow:
    0 4px 12px rgba(0, 212, 255, 0.4),
    0 2px 6px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  animation: badge-float 2s ease-in-out infinite;
  z-index: 3;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

@keyframes badge-float {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-3px) scale(1.05);
  }
}

.quick-action-btn.profile-btn.ai-powered:hover .ai-badge-floating {
  animation-duration: 1s;
  transform: translateY(-5px) scale(1.1);
  box-shadow:
    0 6px 16px rgba(0, 212, 255, 0.6),
    0 3px 8px rgba(0, 0, 0, 0.3);
}

.profile-btn-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transition: left 0.8s ease;
  z-index: 1;
}

.quick-action-btn.profile-btn:hover .profile-btn-shine {
  left: 100%;
}

/* Feed Layout */
.feed-layout {
  display: block;
}

.main-feed {
  min-width: 0;
}

.feed-section {
  background: #fff;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.feed-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
}

.feed-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Feed icon styles removed - using emoji now */

.refresh-btn {
  background: none;
  border: 2px solid #38bdf8;
  color: #0c4a6e;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.refresh-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #38bdf8, #7dd3fc);
  color: #0c4a6e;
  transform: scale(1.1) rotate(180deg);
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 3rem 0;
  color: #666;
}

.loading-spinner {
  font-size: 2rem;
  color: #0c4a6e;
  margin-bottom: 1rem;
}

/* Activity Feed */
.activity-feed {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.activity-card {
  border: 1px solid #e9ecef;
  border-radius: 0.75rem;
  padding: 1.25rem;
  background: #fff;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.75rem;
  position: relative;
  overflow: hidden;
}

.activity-card:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #0c4a6e, #38bdf8, #e0f2fe);
  opacity: 0.8;
}

.activity-card:hover {
  box-shadow: 0 8px 25px rgba(12, 74, 110, 0.15);
  transform: translateY(-3px);
  border-color: #38bdf8;
}

.activity-card:hover:before {
  opacity: 1;
  width: 6px;
}

.activity-card:nth-child(even) {
  background: #fafbfc;
}

.activity-card:nth-child(odd) {
  background: #fff;
}

.activity-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: nowrap;
  min-height: 48px;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.activity-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e9ecef;
}

.activity-meta {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.athlete-name a {
  font-weight: 700;
  color: #333;
  text-decoration: none;
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.athlete-name a:hover {
  color: #4a90e2;
}

.activity-time {
  font-size: 0.8rem;
  color: #666;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.2rem;
  white-space: nowrap;
}

.activity-type-badge {
  background: linear-gradient(135deg, #0c4a6e, #38bdf8);
  color: #fff;
  padding: 0.35rem 0.7rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(12, 74, 110, 0.3);
}

.activity-content {
  margin-bottom: 1rem;
  text-align: center;
  padding: 1rem 0;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 0.5rem;
  margin: 0.75rem 0;
  border: 1px solid rgba(0, 0, 0, 0.03);
}

.activity-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.75rem;
  text-align: center;
  line-height: 1.4;
}

/* AI Generated Image Styles */
.ai-image-container {
  position: relative;
  margin: 1.5rem 0;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.1);
  background: linear-gradient(145deg, #f8f9fa, #ffffff);
  padding: 0.5rem;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.ai-image-container:hover {
  transform: translateY(-4px);
  box-shadow:
    0 12px 40px rgba(0, 0, 0, 0.15),
    0 4px 12px rgba(0, 0, 0, 0.1);
}

.ai-image-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #667eea, #764ba2, #f093fb);
  border-radius: 1rem;
  z-index: -1;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.ai-image-container:hover::before {
  opacity: 0.1;
}

.ai-generated-image {
  width: 100%;
  height: auto;
  max-height: 350px;
  object-fit: cover;
  border-radius: 0.75rem;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  filter: contrast(1.05) saturate(1.1);
}

.ai-generated-image:hover {
  transform: scale(1.015);
  filter: contrast(1.1) saturate(1.15) brightness(1.02);
}

.ai-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 1.5rem;
  font-size: 0.8rem;
  font-weight: 700;
  box-shadow:
    0 4px 15px rgba(102, 126, 234, 0.4),
    0 2px 8px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: flex;
  align-items: center;
  gap: 0.25rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.ai-badge::before {
  content: '✨';
  font-size: 0.9em;
  animation: sparkle 2s ease-in-out infinite;
}

.ai-badge:hover {
  transform: translateY(-2px) scale(1.05);
  box-shadow:
    0 6px 25px rgba(102, 126, 234, 0.5),
    0 4px 12px rgba(0, 0, 0, 0.2);
}

@keyframes sparkle {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
}

/* AI Generated Video Styles */
.ai-video-container {
  position: relative;
  margin: 1.5rem 0;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.1);
  background: linear-gradient(145deg, #f8f9fa, #ffffff);
  padding: 0.5rem;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.ai-video-container:hover {
  transform: translateY(-2px);
  box-shadow:
    0 8px 30px rgba(0, 0, 0, 0.12),
    0 3px 8px rgba(0, 0, 0, 0.08);
}

.ai-video-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #667eea, #764ba2, #f093fb);
  border-radius: 1rem;
  z-index: -1;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.ai-video-container:hover::before {
  opacity: 0.05;
}

.video-wrapper {
  position: relative;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #000;
}

.ai-generated-video {
  width: 100%;
  height: auto;
  max-height: 400px;
  display: block;
  border-radius: 0.75rem;
  transition: all 0.3s ease;
}

.video-controls-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.play-button-overlay {
  pointer-events: auto;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 3px solid rgba(255, 255, 255, 0.3);
}

.play-button-overlay:hover {
  background: rgba(0, 0, 0, 0.8);
  transform: scale(1.1);
  border-color: rgba(255, 255, 255, 0.5);
}

.play-icon {
  color: white;
  font-size: 2rem;
  margin-left: 4px; /* Optical alignment for play button */
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.ai-video-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.image-wrapper {
  position: relative;
  cursor: pointer;
  overflow: hidden;
  border-radius: 0.75rem;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.6));
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  backdrop-filter: blur(2px);
}

.ai-image-container:hover .image-overlay {
  opacity: 1;
}

.overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: white;
  text-align: center;
  transform: translateY(10px);
  transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.ai-image-container:hover .overlay-content {
  transform: translateY(0);
}

.zoom-icon {
  font-size: 2rem;
  animation: pulse 2s ease-in-out infinite;
}

.view-text {
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.activity-stats {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  padding: 0.5rem 0;
}

.stat-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  font-size: 0.9rem;
  color: #666;
  background: #f8f9fa;
  padding: 0.4rem 0.8rem;
  border-radius: 1rem;
  transition: all 0.2s ease;
  min-width: 80px;
}

.stat-item:hover {
  background: rgba(212, 175, 55, 0.2);
  transform: translateY(-1px);
}

.stat-item svg {
  color: #c41e3a;
  font-size: 0.85rem;
}

/* Activity Actions */
.activity-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding: 1rem 0 0.5rem 0;
  margin-top: 0.75rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(248, 249, 250, 0.5);
  border-radius: 0 0 0.5rem 0.5rem;
  margin-left: -1.25rem;
  margin-right: -1.25rem;
  margin-bottom: -1.25rem;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}

.action-btn {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem;
  border-radius: 1rem;
  transition: all 0.3s ease;
  font-size: 0.9rem;
}

.action-btn:hover {
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.2), rgba(224, 242, 254, 0.2));
  color: #0c4a6e;
}

.like-btn.liked {
  color: #e74c3c;
}

.like-btn .fa-heart.liked {
  color: #e74c3c;
}

/* AI Gen Button - Attractive magical styling */
.ai-gen-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white !important;
  border: none;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.ai-gen-btn:hover {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  color: white !important;
}

.ai-gen-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}

.ai-gen-btn .btn-icon {
  color: #ffd700;
  text-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
  animation: sparkle 2s ease-in-out infinite;
}

.ai-gen-btn .action-text {
  color: white;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

/* Sparkle animation for magic icon */
@keyframes sparkle {
  0%, 100% {
    transform: scale(1);
    text-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
  }
  50% {
    transform: scale(1.1);
    text-shadow: 0 0 15px rgba(255, 215, 0, 0.8);
  }
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #666;
}

.empty-state > span {
  font-size: 4rem;
  margin-bottom: 1rem;
  display: block;
}

.empty-state h3 {
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
  color: #333;
}

.cta-btn {
  padding: 0.75rem 2rem;
  border-radius: 2rem;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  transition: background 0.2s, color 0.2s;
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.1);
  border: none;
  cursor: pointer;
}

.cta-btn.primary {
  background: linear-gradient(135deg, #0c4a6e, #38bdf8);
  color: #fff;
}

/* Load More Section */
.load-more-container {
  display: flex;
  justify-content: center;
  margin: 2rem 0;
  padding: 1rem;
}

.load-more-btn {
  background: linear-gradient(135deg, #c41e3a, #d4af37);
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 15px rgba(196, 30, 58, 0.3);
}

.load-more-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(196, 30, 58, 0.4);
}

.load-more-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  font-size: 1.2rem;
  animation: runner 1.5s ease-in-out infinite;
}

@keyframes runner {
  0%, 100% {
    transform: translateX(0) scale(1);
  }
  25% {
    transform: translateX(3px) scale(1.1);
  }
  50% {
    transform: translateX(0) scale(1);
  }
  75% {
    transform: translateX(-3px) scale(1.1);
  }
}

.cta-btn.primary:hover {
  background: linear-gradient(135deg, #38bdf8, #0c4a6e);
  transform: translateY(-2px);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .feed-container {
    padding: 0 0.5rem;
  }
}

@media (max-width: 768px) {
  .welcome-content {
    flex-direction: column;
    text-align: center;
  }

  .user-greeting {
    flex-direction: column;
    text-align: center;
  }

  .greeting-title {
    font-size: 1.5rem;
  }

  .quick-actions {
    justify-content: center;
  }

  .quick-action-btn.profile-btn.ai-powered {
    min-width: 180px;
    height: 55px;
    border-radius: 28px;
  }

  .profile-icon-wrapper {
    width: 40px;
    height: 40px;
  }

  .profile-icon.ai-icon {
    font-size: 18px;
  }

  .profile-label {
    font-size: 16px;
  }

  .profile-subtitle {
    font-size: 12px;
  }

  .profile-arrow {
    width: 32px;
    height: 32px;
  }

  .arrow-icon.ai-sparkle {
    font-size: 14px;
  }

  .ai-badge-floating {
    font-size: 9px;
    padding: 3px 6px;
    top: -6px;
    right: -6px;
  }

  .activity-card {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }

  .activity-card:before {
    width: 3px;
  }

  .activity-card:hover:before {
    width: 4px;
  }

  .activity-header {
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    min-height: 40px;
  }

  .activity-avatar {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
  }

  .athlete-name a {
    font-size: 0.9rem;
    max-width: 120px;
  }

  .activity-time {
    font-size: 0.75rem;
    gap: 0.2rem;
  }

  .activity-type-badge {
    padding: 0.25rem 0.5rem;
    font-size: 0.7rem;
    border-radius: 0.5rem;
  }

  .activity-content {
    padding: 0.25rem 0;
  }

  .activity-title {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }

  .activity-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    gap: 0.75rem;
    justify-items: center;
    align-items: center;
    max-width: 320px;
    margin: 0 auto;
    padding: 0.5rem 0;
  }

  .stat-item {
    font-size: 0.8rem;
    padding: 0.5rem 0.7rem;
    min-width: 100px;
    width: 100%;
    max-width: 140px;
    text-align: center;
    border-radius: 0.75rem;
    background: linear-gradient(135deg, #f8f9fa, #ffffff);
    border: 1px solid rgba(74, 144, 226, 0.1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
  }

  .stat-item:hover {
    background: linear-gradient(135deg, #e9ecef, #f8f9fa);
    border-color: rgba(74, 144, 226, 0.25);
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
  }

  .stat-item svg {
    font-size: 0.75rem;
  }

  /* Hide cadence stat on mobile tablets */
  .activity-stats .cadence-stat {
    display: none;
  }

  .ai-image-container {
    margin: 1rem 0;
    padding: 0.25rem;
  }

  .ai-generated-image {
    max-height: 250px;
  }

  .ai-badge {
    font-size: 0.7rem;
    padding: 0.3rem 0.5rem;
    top: 0.75rem;
    right: 0.75rem;
  }

  .ai-badge::before {
    font-size: 0.8em;
  }

  .image-overlay {
    backdrop-filter: blur(1px);
  }

  .zoom-icon {
    font-size: 1.5rem;
  }

  .view-text {
    font-size: 0.8rem;
  }

  .activity-actions {
    margin-left: -1rem;
    margin-right: -1rem;
    margin-bottom: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

@media (max-width: 480px) {
  .activity-header {
    gap: 0.4rem;
  }

  .activity-avatar {
    width: 36px;
    height: 36px;
  }

  .athlete-name a {
    font-size: 0.85rem;
    max-width: 100px;
  }

  .activity-time {
    font-size: 0.7rem;
  }

  .activity-type-badge {
    padding: 0.2rem 0.4rem;
    font-size: 0.65rem;
  }

  .load-more-container {
    margin: 1.5rem 0;
    padding: 0.5rem;
  }

  .load-more-btn {
    padding: 0.65rem 1.5rem;
    font-size: 0.9rem;
  }

  .quick-action-btn.profile-btn.ai-powered {
    min-width: 160px;
    height: 50px;
    border-radius: 25px;
  }

  .profile-icon-wrapper {
    width: 36px;
    height: 36px;
  }

  .profile-icon.ai-icon {
    font-size: 16px;
  }

  .profile-label {
    font-size: 15px;
  }

  .profile-subtitle {
    font-size: 11px;
  }

  .profile-arrow {
    width: 28px;
    height: 28px;
  }

  .arrow-icon.ai-sparkle {
    font-size: 12px;
  }

  .ai-badge-floating {
    font-size: 8px;
    padding: 2px 5px;
    top: -5px;
    right: -5px;
  }

  .activity-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    gap: 0.5rem;
    justify-items: center;
    align-items: center;
    max-width: 280px;
    margin: 0 auto;
  }

  .stat-item {
    font-size: 0.75rem;
    padding: 0.4rem 0.6rem;
    min-width: 90px;
    width: 100%;
    max-width: 120px;
    text-align: center;
    border-radius: 0.75rem;
    background: linear-gradient(135deg, #f8f9fa, #ffffff);
    border: 1px solid rgba(74, 144, 226, 0.1);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
  }

  .stat-item:hover {
    background: linear-gradient(135deg, #e9ecef, #f8f9fa);
    border-color: rgba(74, 144, 226, 0.2);
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  /* Hide cadence stat on small mobile devices */
  .activity-stats .cadence-stat {
    display: none;
  }

  /* Mobile AI Video Styles */
  .ai-video-container {
    margin: 1rem 0;
    padding: 0.25rem;
  }

  .ai-generated-video {
    max-height: 250px;
  }

  .play-button-overlay {
    width: 60px;
    height: 60px;
  }

  .play-icon {
    font-size: 1.5rem;
  }

  .ai-video-badge {
    font-size: 0.7rem;
    padding: 0.3rem 0.5rem;
    top: 0.75rem;
    right: 0.75rem;
  }
}

/* Image Modal Styles */
.image-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.image-modal-content {
  background: #fff;
  border-radius: 1rem;
  max-width: 90vw;
  max-height: 90vh;
  width: auto;
  height: auto;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.3),
    0 8px 16px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  animation: slideUp 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
}

@keyframes slideUp {
  from {
    transform: translateY(20px) scale(0.95);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e9ecef;
  background: linear-gradient(135deg, #f8f9fa, #ffffff);
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #333;
  margin: 0;
  flex: 1;
  padding-right: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modal-close-btn {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.modal-close-btn:hover {
  background: #f8f9fa;
  color: #333;
  transform: scale(1.1);
}

.modal-body {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
}

.modal-image {
  width: 100%;
  height: auto;
  max-height: 70vh;
  object-fit: contain;
  display: block;
}

.modal-badge {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 2rem;
  font-size: 0.9rem;
  font-weight: 700;
  box-shadow:
    0 4px 15px rgba(102, 126, 234, 0.4),
    0 2px 8px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.modal-badge::before {
  content: '✨';
  font-size: 1em;
  animation: sparkle 2s ease-in-out infinite;
}

/* Mobile Modal Styles */
@media (max-width: 768px) {
  .image-modal-overlay {
    padding: 0.5rem;
  }

  .image-modal-content {
    max-width: 95vw;
    max-height: 95vh;
    border-radius: 0.75rem;
  }

  .modal-header {
    padding: 1rem 1.5rem;
  }

  .modal-title {
    font-size: 1.1rem;
  }

  .modal-close-btn {
    width: 36px;
    height: 36px;
  }

  .modal-image {
    max-height: 75vh;
  }

  .modal-badge {
    bottom: 0.75rem;
    right: 0.75rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .modal-header {
    padding: 0.75rem 1rem;
  }

  .modal-title {
    font-size: 1rem;
  }

  .modal-close-btn {
    width: 32px;
    height: 32px;
  }

  .modal-image {
    max-height: 80vh;
  }

  .modal-badge {
    bottom: 0.5rem;
    right: 0.5rem;
    padding: 0.4rem 0.6rem;
    font-size: 0.75rem;
  }
}
</style>
